/**
 * Word form helpers shared by the form page, the schedule list and the stores.
 * Pure functions over the contract types — no React, no fetches.
 */

import { ApiRequestError } from '@/lib/api'
import { formatFileSize } from '@/lib/schemas'
import { docxTargetOf } from '@/lib/fill-source'
import type { DocxFieldFillTarget, FillProposal } from '@/types/schedule'
import type {
  DocxFillRejection,
  FormField,
  FormHeading,
  FormSchedule,
  FormScheduleCorrectionReason,
} from '@/types/word-form'

// ===================
// Schedules
// ===================

/** Form page query param naming the schedule to open (`?schedule=`). */
export const SCHEDULE_PARAM = 'schedule'

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

/** Schedules are found in Word files only (plan §3.5). */
export function isWordDocument(mimeType: string | null | undefined): boolean {
  return mimeType === DOCX_MIME
}

/** Matches the `.docx` cap in ALLOWED_FILE_TYPES. */
export const MAX_WORD_FORM_BYTES = 50 * 1024 * 1024

/**
 * A .docx we can fill as a form. Extension first (uploads often arrive as
 * octet-stream), MIME as the fallback. Mirrors `isWordFormSource` in
 * `backend/src/lib/forms/word-form.ts`; keep the two in step.
 */
export function isWordFormSource(filename: string, mimeType: string): boolean {
  const lower = filename.toLowerCase()
  if (lower.endsWith('.docm') || lower.endsWith('.doc') || lower.endsWith('.dotx')) return false
  return lower.endsWith('.docx') || mimeType.toLowerCase() === DOCX_MIME
}

/** A user-facing message for a file that can't be filled as a form, or null. */
export function validateWordFormFile(file: File): string | null {
  if (file.name.toLowerCase().endsWith('.doc')) {
    return 'Older .doc files are not supported. Save it as .docx in Word and try again.'
  }
  if (!isWordFormSource(file.name, file.type)) {
    return 'Choose a Word (.docx) file.'
  }
  if (file.size > MAX_WORD_FORM_BYTES) {
    return `That file is ${formatFileSize(file.size)}. The maximum is ${formatFileSize(MAX_WORD_FORM_BYTES)}.`
  }
  return null
}

/**
 * What "Fill with AI" asks the assistant. `null` = fields outside every
 * schedule. The title is the heading as written ("Schedule 3 – Referees"), so
 * it is used as-is — never prefixed with "Schedule".
 */
export function fillUtterance(schedule: Pick<FormSchedule, 'title'> | null): string {
  return schedule
    ? `Fill the fields in ${schedule.title} from our company profile and library.`
    : 'Fill the fields outside the schedules in this form from our company profile and library.'
}

/** "Not started", "2 of 5 fields" or "Done". */
export function scheduleStatusLabel(schedule: FormSchedule): string {
  if (schedule.status === 'done') return 'Done'
  if (schedule.status === 'not_started') return 'Not started'
  const { filled, fillable } = schedule.fieldCounts
  return `${filled} of ${fillable} ${fillable === 1 ? 'field' : 'fields'}`
}

/** Fields belonging to one schedule. `null` = fields outside every schedule. */
export function fieldsInSchedule(fields: FormField[], scheduleId: string | null): FormField[] {
  return fields.filter((field) => field.scheduleId === scheduleId)
}

/**
 * Signatures, declarations, prices and anything we can't write safely are
 * never filled here — the field model marks them manual or unsupported.
 */
export function isFillable(field: FormField): boolean {
  return field.support === 'fillable'
}

// ===================
// Proposals
// ===================

/**
 * The live proposal for a field: a pending one first (it is what needs a
 * decision), then the newest accepted. Superseded and dismissed rows are history.
 */
export function proposalForField(
  proposals: FillProposal[],
  fieldId: string
): FillProposal | null {
  const forField = proposals.filter((p) => docxTargetOf(p)?.fieldId === fieldId)
  const pending = forField.find((p) => p.status === 'pending')
  if (pending) return pending
  const accepted = forField
    .filter((p) => p.status === 'accepted')
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  if (accepted[0]) return accepted[0]
  return forField.find((p) => p.status === 'dismissed') ?? null
}

/** How a checkbox value reads to a person. Booleans travel as strings. */
export function checkboxLabel(value: string | null): string {
  return value === 'true' ? 'Ticked' : 'Not ticked'
}

export function isCheckboxField(field: Pick<FormField, 'kind'>): boolean {
  return field.kind === 'checkbox' || field.kind === 'legacy_checkbox'
}

/** The `docx_field` target for a field of this file version. */
export function docxFieldTarget(field: FormField, sourceSha256: string): DocxFieldFillTarget {
  return {
    kind: 'docx_field',
    fieldId: field.fieldId,
    sourceSha256,
    fingerprint: field.fingerprint,
  }
}

/** A typed answer as the field takes it: checkboxes as booleans, the rest as text. */
export function manualFillValue(field: Pick<FormField, 'kind'>, draft: string): string | boolean {
  return isCheckboxField(field) ? draft === 'true' : draft
}

// ===================
// Error copy (contract §4 `details.reason`)
// ===================

const FILL_REFUSAL_COPY: Record<DocxFillRejection, string> = {
  source_mismatch: 'This form has changed since it was read. Reload the page and try again.',
  unknown_field: "This field isn't in the form any more. Reload the page.",
  model_mismatch: 'This form has changed since it was read. Reload the page and try again.',
  not_fillable: 'This field has to be filled in Word.',
  wrong_value_type: "That answer doesn't fit this field.",
  option_not_in_list: "That isn't one of this field's options.",
  empty_value: 'Type an answer first.',
  value_too_long: 'That answer is too long for this field.',
  multiline_not_allowed: 'This field takes a single line.',
}

/** Fallback when an answer is refused for a reason we don't recognise. */
export const FILL_REFUSED = "That answer couldn't be saved. Try again."

function isFillRejection(reason: unknown): reason is DocxFillRejection {
  return typeof reason === 'string' && reason in FILL_REFUSAL_COPY
}

/** Plain copy for a refused answer, from its reason code. */
export function fillRefusalMessage(reason: unknown, fallback = FILL_REFUSED): string {
  return isFillRejection(reason) ? FILL_REFUSAL_COPY[reason] : fallback
}

/**
 * Why an accepted answer was left out of the completed copy. `applyError`
 * carries the server's code, so never show it raw.
 */
export function applyErrorMessage(code: string): string {
  return fillRefusalMessage(code, "This answer couldn't be written into the form. Check it and try again.")
}

const CORRECTION_COPY: Record<FormScheduleCorrectionReason, string> = {
  schedule_not_found: 'That schedule has changed. Reload the page and try again.',
  path_outside_document: "That point isn't in the main part of the form. Pick another.",
  invalid_range: 'The end has to come after the start. Pick again.',
  not_adjacent: 'Only schedules next to each other can be merged.',
  overlaps_schedule: 'That overlaps another schedule. Split or merge that one instead.',
}

/** Plain copy for a refused schedule correction (`details.reason`). */
export function scheduleCorrectionMessage(error: unknown): string {
  const reason = reasonOf(error)
  if (reason && reason in CORRECTION_COPY) {
    return CORRECTION_COPY[reason as FormScheduleCorrectionReason]
  }
  return "Couldn't save that change. Try again."
}

/** `details.reason` off an API error, if it has one. */
export function reasonOf(error: unknown): string | null {
  if (!(error instanceof ApiRequestError)) return null
  const reason = error.details?.['reason']
  return typeof reason === 'string' ? reason : null
}

// ===================
// Split / add points
// ===================

/** A point a schedule can start at: a heading when we have one, else a field. */
export interface SchedulePoint {
  path: string
  label: string
}

function headingPoints(headings: FormHeading[]): SchedulePoint[] {
  return headings.map((h) => ({ path: h.path, label: h.text }))
}

function fieldPoints(fields: FormField[]): SchedulePoint[] {
  return fields.map((f) => ({ path: f.fieldId, label: f.label }))
}

/**
 * Where a schedule can be split: the headings after its own and before the
 * next schedule's. Headings come in document order, so this needs no layout.
 * Falls back to the schedule's fields (after its first) when its start isn't
 * a heading or no heading sits inside it.
 */
export function splitPoints(
  schedule: FormSchedule,
  schedules: FormSchedule[],
  headings: FormHeading[],
  fields: FormField[]
): SchedulePoint[] {
  const start = headings.findIndex((h) => h.path === schedule.startPath)
  if (start >= 0) {
    const otherStarts = new Set(schedules.filter((s) => s.id !== schedule.id).map((s) => s.startPath))
    const inside: FormHeading[] = []
    for (const heading of headings.slice(start + 1)) {
      if (otherStarts.has(heading.path)) break
      inside.push(heading)
    }
    if (inside.length > 0) return headingPoints(inside)
  }
  return fieldPoints(fields.filter((f) => f.scheduleId === schedule.id).slice(1))
}

/**
 * Where a missed schedule can start: any heading that doesn't already start
 * one. Falls back to the form's fields when the file has no headings.
 */
export function addStartPoints(
  schedules: FormSchedule[],
  headings: FormHeading[],
  fields: FormField[]
): SchedulePoint[] {
  if (headings.length > 0) {
    const starts = new Set(schedules.map((s) => s.startPath))
    return headingPoints(headings.filter((h) => !starts.has(h.path)))
  }
  return fieldPoints(fields.filter((f) => f.location === 'body'))
}

/**
 * Where a missed schedule ends: its last field. The end is inclusive and the
 * server widens a field to the block it sits in, so a field is the honest way
 * to say "up to and including this" — a heading would end on the heading.
 */
export function addEndPoints(fields: FormField[]): SchedulePoint[] {
  return fieldPoints(fields.filter((f) => f.location === 'body'))
}

// ===================
// Long answer for its cell (plan §3.6)
// ===================

/** Average character width of 11 pt body text, in twips (1 pt = 20 twips). */
const AVERAGE_CHAR_TWIPS = 110
/** Roughly one A4 page of 11 pt body text. */
const LINES_PER_PAGE = 45
/** Warn from about two-thirds of a page: a box that size is rarely intended. */
const LONG_ANSWER_LINES = 30

/** Lines an answer would take in a cell of this width — a rough, honest estimate. */
export function estimateAnswerLines(value: string, cellWidthTwips: number): number {
  if (cellWidthTwips <= 0) return 0
  const charsPerLine = Math.max(1, Math.floor(cellWidthTwips / AVERAGE_CHAR_TWIPS))
  return value
    .split('\n')
    .reduce((lines, paragraph) => lines + Math.max(1, Math.ceil(paragraph.length / charsPerLine)), 0)
}

/**
 * Null when the answer fits, otherwise the user-facing warning. Fields with
 * no measured width never warn — we would only be guessing.
 */
export function longAnswerWarning(value: string, cellWidthTwips: number | null): string | null {
  if (cellWidthTwips === null || value.trim().length === 0) return null
  const lines = estimateAnswerLines(value, cellWidthTwips)
  if (lines < LONG_ANSWER_LINES) return null
  const pages = Math.max(1, Math.round(lines / LINES_PER_PAGE))
  return pages > 1
    ? `Long for its box — this could run to about ${pages} pages in the form.`
    : 'Long for its box — this could take most of a page in the form.'
}
